<script setup lang="ts">
import Pdf from "@/components/Pdf.vue"
import type { ChampionshipRegistrationTeam } from "@/model/championship/championship-registration-team"
import { ChampionshipTeamUploadState } from "@/model/championship/championship-team-upload-state"
import {
    type ChampionshipRegistrationFileInfo,
    useChampionshipRegistrationSelectTeamData,
} from "@/pages/Tasks/Tasks/championship-registration"
import { useRegistrationPolicy } from "@/pages/Tasks/Tasks/useRegistrationPolicy"
import { defineTranslations } from "@/utils/defineTranslations"
import { $fileSize, useApp, useHttpClient, useUpload } from "@/vf"
import type { AxiosError } from "axios"
import { computed, ref } from "vue"

const props = defineProps<{
    registrationId: string
    team: ChampionshipRegistrationTeam
    file: ChampionshipRegistrationFileInfo
    viewMode: "customer" | "admin"
}>()

const emit = defineEmits<{
    (e: "updated"): void
}>()

const http = useHttpClient()
const { apiLink } = useApp()
const data = useChampionshipRegistrationSelectTeamData()
const policy = useRegistrationPolicy(computed(() => data.value?.permissions), computed(() => props.viewMode))

/*─────────────────────────────────────┐
│  Upload (User)                       │
└─────────────────────────────────────*/
const uploadErrors = ref<string[]>([])

async function uploadFile() {
    if (!props.file.isUploadable) {
        return
    }

    const upload = useUpload({
        url: `/championship/registration/${props.registrationId}/upload-team-file/${props.team.id}/${props.file.id}?viewMode=${props.viewMode}`,
        method: "post",
        fieldName: "file",
    })

    upload.finished
        .then(() => {
            uploadErrors.value = []
            emit("updated")
        })
        .catch((error: AxiosError<any>) => {
            const body = error.response?.data as { _: unknown } | undefined

            // If API returns codes/messages under _ as an array, surface them
            if (body && Array.isArray(body._)) {
                uploadErrors.value = body._.filter((m): m is string => typeof m === "string")
                return
            }

            // Fallback: show a single generic message from API or error
            const generic = (typeof (body as any)?._ === "string" && [(body as any)._]) || [
                error.message || "Upload failed",
            ]

            uploadErrors.value = generic
        })
}

/*─────────────────────────────────────┐
│  Approval (admin)                    │
└─────────────────────────────────────*/
const approvalSpinner = ref(false)
const approvalError = ref<string | null>(null)

async function approveFile(approve: boolean) {
    approvalSpinner.value = true
    try {
        await http.post(
            `/championship/registration/${props.registrationId}/approve-team-file/${props.team.id}/${props.file.id}?viewMode=${props.viewMode}`,
            { approve, uploadedAt: props.file.uploadedAt },
        )
        emit("updated")
    } catch (e) {
        const error = (e as AxiosError<{ error: string }>).response?.data?.error
        const status = (e as AxiosError).response?.status

        if (status === 409 && error === "changed") {
            approvalError.value = "changed"
            emit("updated")
            return
        }
    } finally {
        approvalSpinner.value = false
    }
}

/*─────────────────────────────────────┐
│  Preview                             │
└─────────────────────────────────────*/
const previewUrl = computed(() => {
    return `/championship/registration/${props.registrationId}/download-team-file/${props.team.id}/${props.file.id}`
})

const showPreview = computed(() => {
    if (!props.file.originalName) {
        return false
    }

    switch (props.file.type) {
        case "Image":
        case "Music":
            return true
        case "Document":
            return props.file.originalName.toLowerCase().endsWith(".pdf")
        default:
            return false
    }
})

const trans = defineTranslations({
    error_filetype: {
        de: "Die Datei hat einen ungültigen Dateitypen.",
        en: "The file has an invalid file type.",
    },
    error_filesize: {
        de: "Die Datei ist zu groß. Bitte lade maximal 5 MB hoch.",
        en: "The file is too large. Please upload a maximum of 5 MB.",
    },
    file_changed: {
        de: "Die Datei wurde zwischenzeitlich geändert. Bitte erneut prüfen.",
        en: "The file has been changed in the meantime. Please check again.",
    },
    approve_file: {
        de: "Genehmigen",
        en: "Approve",
    },
    reject_file: {
        de: "Ablehnen",
        en: "Reject",
    },
    upload: {
        de: "Hochladen",
        en: "Upload",
    },
    download: {
        de: "Herunterladen",
        en: "Download",
    },
})
</script>

<template>
    <div class="pl-5 pb-5 pt-3">
        <div class="row">
            <!-- Left side: actions and download -->
            <div class="col-12" :class="{ 'col-lg-6': showPreview }">
                <!-- Upload errors -->
                <div v-if="uploadErrors.length > 0" class="text-danger small mb-2">
                    <span v-for="error in uploadErrors" :key="error" class="d-block">
                        <span v-if="error === 'filetype'">{{ trans("error_filetype") }}</span>
                        <span v-else-if="error === 'size'">{{ trans("error_filesize") }}</span>
                        <span v-else>{{ error }}</span>
                    </span>
                </div>

                <!-- File download link -->
                <div v-if="file.originalName" class="mb-3">
                    <a :href="apiLink(previewUrl)">
                        <i class="fas fa-download mr-1"></i>
                        {{ file.originalName }} ({{ $fileSize(file.fileSize!) }})
                    </a>
                </div>

                <!-- Download button -->
                <a v-if="file.originalName" :href="apiLink(previewUrl)" class="btn btn-primary">
                    <i class="fas fa-download mr-1"></i>
                    {{ trans("download") }}
                </a>

                <!-- Upload button -->
                <button
                    type="button"
                    class="btn btn-primary mr-2"
                    @click="uploadFile()"
                    v-if="file.isUploadable && policy.UploadTeamFile"
                >
                    <i class="fas fa-upload mr-1"></i>
                    {{ trans("upload") }}
                </button>

                <!-- Admin approval buttons -->
                <template v-if="viewMode === 'admin' && file.state === ChampionshipTeamUploadState.Pending">
                    <div v-if="approvalError" class="mb-2">
                        <span class="text-danger">
                            {{ trans("file_changed") }}
                        </span>
                    </div>

                    <template v-if="approvalSpinner">
                        <i class="fas fa-spinner fa-spin"></i>
                    </template>
                    <template v-else>
                        <button type="button" class="btn btn-primary mr-2" @click="approveFile(true)" v-if="policy.ApproveTeamFile">
                            {{ trans("approve_file") }}
                        </button>
                        <button type="button" class="btn btn-danger" @click="approveFile(false)" v-if="policy.ApproveTeamFile">
                            {{ trans("reject_file") }}
                        </button>
                    </template>
                </template>
            </div>

            <!-- Right side: preview -->
            <div v-if="showPreview" class="col-12 col-lg-6">
                <!-- Image preview -->
                <img
                    v-if="file.type === 'Image'"
                    :src="apiLink(previewUrl)"
                    :alt="file.originalName"
                    class="img-fluid"
                />

                <!-- Audio preview -->
                <audio v-if="file.type === 'Music'" :src="apiLink(previewUrl)" controls class="w-100"></audio>

                <!-- PDF preview -->
                <Pdf v-if="file.type === 'Document'" :file="previewUrl" paginated />
            </div>
        </div>
    </div>
</template>
